// /content/versions/:id/edit (E31-S6-T2; ADR-035; mockups/content-edit.html): the drafter's editor for a
// draft, fresh or sent back. A form, not YAML: the approver's note as a banner, each scene's still,
// highlight box, narration and guide page, the question set as Markdown beside the pack parser's live
// check, and the content version with the D-76 question in the catalogue's words. Save is one PUT of
// the whole first-draft body (edit.ts buildBody); Submit for review is the submit POST, after a save
// when there are unsaved changes. Nothing on the page writes a sentence of its own about the
// application: the scene text and questions are what the drafter typed.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Principal } from "@waypoint/contracts";
import { Button, cn, focusRing, Icon } from "@waypoint/surface";
import { useEffect, useMemo, useState } from "react";
import { flushSync } from "react-dom";
import { Link, useLocation } from "wouter";
import { ConflictError, ForbiddenError, NotFoundError, ValidationError } from "../../api/errors.js";
import { useApi } from "../../api/provider.js";
import { t } from "../../i18n/t.js";
import { FieldError, Label, StatusChip } from "../../ui/form.js";
import {
  BODY_MAX_BYTES,
  bodySize,
  buildBody,
  canEdit,
  centredBox,
  draftOf,
  fieldOf,
  hasStill,
  holdStill,
  questionProblems,
  replacedStillId,
  reviewPath,
  sceneProblems,
  STILL_MAX_BYTES,
  type FieldPlace,
  type HeldStill,
  type SceneDraft,
} from "../../versions/edit.js";
import {
  secondsFor,
  stillPath,
  trailOf,
  VersionDetail,
  VersionList,
  versionPath,
  versionsPath,
  wordsOf,
} from "../../versions/model.js";
import { usePlan, useVersion, versionKey, versionsKey } from "../../versions/queries.js";
import { EmptyState, Page } from "../page.js";
import { formatWhen } from "./issue-format.js";
import { mmss } from "./plan-model.js";
import { focusLine, QUESTIONS_ID, QuestionsEditor } from "./questions-editor.js";
import { CARD } from "./review-pane.js";
import { FIELD, mb, SceneEditor, sceneMessages, type SceneMessages } from "./scene-editor.js";
import { useAnnouncer } from "./scripts-page.js";
import { Seg, titleInfoOf, trailWords, whoOf } from "./script-format.js";

const DEBOUNCE_MS = 250;
const LEAD = "m-0 text-13 text-wp-text-2";

function useDebounced<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSettled(value);
    }, ms);
    return () => {
      window.clearTimeout(timer);
    };
  }, [value, ms]);
  return settled;
}

/** A browser address for each held still's bytes, for its preview; none where the browser has none. */
function useStillUrls(held: ReadonlyMap<string, HeldStill>): ReadonlyMap<string, string> {
  const [urls, setUrls] = useState<ReadonlyMap<string, string>>(new Map());
  // The effect makes the addresses and revokes the same ones, so a re-run (StrictMode) never leaves a
  // preview pointing at one already revoked.
  useEffect(() => {
    const made = new Map<string, string>();
    if (typeof URL.createObjectURL === "function")
      for (const [id, s] of held) {
        try {
          made.set(
            id,
            URL.createObjectURL(new Blob([s.bytes as BlobPart], { type: s.contentType })),
          );
        } catch {
          // No preview where the bytes can't be made a Blob (jsdom across realms); the form still works.
        }
      }
    setUrls(made);
    return () => {
      for (const u of made.values()) URL.revokeObjectURL(u);
    };
  }, [held]);
  return urls;
}

/** The stills a version's scenes name, each fetched, read and hashed, ready to go back in the body. */
function useHeldStills(version: VersionDetail | undefined) {
  const api = useApi();
  const id = version?.id;
  return useQuery({
    queryKey: ["versions", "stills", id ?? ""],
    enabled: version !== undefined && id !== undefined && version.state === "draft",
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const ids = [...new Set(version?.script.scenes.filter(hasStill).map((s) => s.stillId) ?? [])];
      const held = await Promise.all(
        ids.map(async (stillId) => {
          const { bytes } = await api.bytes(stillPath(id ?? "", stillId));
          const still = await holdStill(stillId, bytes);
          if ("refused" in still) throw new Error(`still ${stillId} can't be read`);
          return [stillId, still] as const;
        }),
      );
      return new Map<string, HeldStill>(held);
    },
  });
}

/** The content version served to learners now, from the title's list. */
function useServedContentVersion(titleId: string | undefined) {
  const api = useApi();
  const list = useQuery({
    queryKey: versionsKey(titleId ?? ""),
    queryFn: () => api.get(versionsPath(titleId ?? ""), VersionList),
    enabled: titleId !== undefined,
  });
  return list.data?.versions.find((v) => v.served)?.contentVersion;
}

export function EditPage({ id, principal }: { id: string; principal: Principal }) {
  const version = useVersion(id);
  const plan = usePlan();
  const stills = useHeldStills(version.data);
  const served = useServedContentVersion(version.data?.titleId);
  const [submitted, setSubmitted] = useState<VersionDetail | undefined>(undefined);
  const { say, region } = useAnnouncer();

  if (version.isPending)
    return (
      <Page title={t("edit.eyebrow")}>
        <p role="status" aria-busy className="text-wp-text-3">
          {t("edit.loading")}
        </p>
      </Page>
    );
  if (version.isError) {
    const err = version.error;
    return (
      <Page title={t(err instanceof ForbiddenError ? "page.forbidden.title" : "edit.failed.title")}>
        <EmptyState
          icon={err instanceof ForbiddenError ? "lock" : "alert"}
          title={
            err instanceof ForbiddenError
              ? t("scripts.forbidden")
              : err instanceof NotFoundError
                ? t("review.notFound")
                : t("review.failed.line")
          }
        />
      </Page>
    );
  }

  const v = version.data;
  const info = titleInfoOf(plan.data, v.titleId, v.pack.id);
  if (submitted !== undefined)
    return (
      <section aria-labelledby="page-title" className="grid gap-4 pb-14">
        <Head version={submitted} title={info.title} />
        <div
          role="status"
          className="rounded-8 border border-wp-accent/44 bg-wp-accent/13 px-3 py-2.5 text-13 text-wp-text"
        >
          <b className="mb-0.5 block">{t("edit.submitted.title")}</b>
          {t("edit.submitted.line")}
          <div className="mt-2">
            <Button asChild compact>
              <Link href={reviewPath(id)}>{t("edit.notDraft.open")}</Link>
            </Button>
          </div>
        </div>
        {region}
      </section>
    );
  if (!canEdit(v, principal))
    return (
      <Page title={t("edit.notDraft.title")}>
        <EmptyState icon="lock" title={t("edit.notDraft.title")} line={t("edit.notDraft.line")}>
          <Button asChild compact className="mt-2">
            <Link href={reviewPath(id)}>{t("edit.notDraft.open")}</Link>
          </Button>
        </EmptyState>
      </Page>
    );
  if (stills.isError)
    return (
      <Page title={info.title}>
        <EmptyState icon="alert" title={t("edit.failed.title")} line={t("edit.stillsFailed")} />
      </Page>
    );
  if (stills.isPending)
    return (
      <Page title={info.title}>
        <p role="status" aria-busy className="text-wp-text-3">
          {t("edit.loadingStills")}
        </p>
      </Page>
    );

  return (
    <>
      <Editor
        key={id}
        version={v}
        title={info.title}
        principal={principal}
        stills={stills.data}
        served={served}
        say={say}
        onSubmitted={(next) => {
          setSubmitted(next);
          say(t("edit.announce.submitted"));
        }}
      />
      {region}
    </>
  );
}

function Head({ version, title }: { version: VersionDetail; title: string }) {
  const dt = "text-11 font-semibold tracking-[0.08em] text-wp-text-3 uppercase";
  const seconds = version.script.scenes.reduce((n, s) => n + secondsFor(wordsOf(s.narration)), 0);
  const facts: [string, string][] = [
    ...(version.guidePages === undefined
      ? []
      : [[t("edit.facts.guidePages"), version.guidePages] as [string, string]]),
    [t("edit.facts.scenes"), String(version.script.scenes.length)],
    [t("edit.facts.runtime"), t("edit.facts.runtimeValue", { time: mmss(seconds) })],
  ];
  return (
    <div className="grid gap-2 pt-1">
      <div className="text-12 font-semibold tracking-[0.16em] text-wp-accent uppercase">
        {t("edit.eyebrow")}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <Seg id={version.titleId} />
        {version.state === "draft" ? (
          <StatusChip tone={version.sentBack === undefined ? "plain" : "stop"}>
            {t(version.sentBack === undefined ? "edit.state.draft" : "edit.state.sentBack")}
          </StatusChip>
        ) : (
          <StatusChip tone="open">{t("edit.state.inReview")}</StatusChip>
        )}
        <StatusChip tone="quiet">{t("edit.version", { n: version.number })}</StatusChip>
      </div>
      <h1
        id="page-title"
        className="m-0 text-[clamp(1.4rem,3vw,1.9rem)] leading-tight font-bold text-wp-text"
      >
        {title}
      </h1>
      <dl className="m-0 flex flex-wrap gap-x-5.5 gap-y-1.5 text-13 text-wp-text-2">
        {facts.map(([term, value]) => (
          <div key={term} className="flex items-baseline gap-1.5">
            <dt className={dt}>{term}</dt>
            <dd className="m-0">{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

type Banner =
  | { readonly kind: "conflict" }
  | { readonly kind: "refused"; readonly mapped: boolean }
  | { readonly kind: "forbidden" }
  | { readonly kind: "failed" }
  | { readonly kind: "submit-refused" };

type Go = { readonly target: string; readonly scene?: number; readonly line?: number };

interface Problem {
  readonly label: string;
  readonly go: Go;
}

const snapshot = (drafts: readonly SceneDraft[], questions: string, cv: string) =>
  JSON.stringify({ drafts, questions, cv: cv.trim() }, (_k, v: unknown) =>
    typeof v === "number" && Number.isNaN(v) ? "NaN" : v,
  );

function Editor({
  version,
  title,
  principal,
  stills,
  served,
  say,
  onSubmitted,
}: {
  version: VersionDetail;
  title: string;
  principal: Principal;
  stills: ReadonlyMap<string, HeldStill>;
  served: string | undefined;
  say: (message: string) => void;
  onSubmitted: (version: VersionDetail) => void;
}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();
  const id = version.id ?? "";
  const guidePages = version.guidePages;
  const first = version.script.scenes[0]?.citation;
  const ctx = {
    titleId: version.titleId,
    doc: first?.doc ?? "",
    docVersion: first?.docVersion ?? "",
    guidePages,
  };

  const [drafts, setDrafts] = useState<readonly SceneDraft[]>(() =>
    version.script.scenes.map(draftOf),
  );
  const [held, setHeld] = useState<ReadonlyMap<string, HeldStill>>(stills);
  /** Scenes whose still was replaced since the last save, in the order they were replaced. */
  const [replaced, setReplaced] = useState<ReadonlyMap<number, number>>(new Map());
  const [refused, setRefused] = useState<
    ReadonlyMap<number, { name: string; reason: "size" | "type"; size: number }>
  >(new Map());
  const [reading, setReading] = useState<ReadonlySet<number>>(new Set());
  const [questions, setQuestions] = useState(version.questions);
  const [cv, setCv] = useState(version.contentVersion);
  const [saved, setSaved] = useState(() =>
    snapshot(version.script.scenes.map(draftOf), version.questions, version.contentVersion),
  );
  const [savedOnce, setSavedOnce] = useState(false);
  const [open, setOpen] = useState(0);
  const [banner, setBanner] = useState<Banner | null>(null);
  const [server, setServer] = useState<readonly FieldPlace[]>([]);
  const [asking, setAsking] = useState(false);
  /** The draft (as its snapshot) whose exact body was over the limit when the estimate was not. */
  const [tooBigAt, setTooBigAt] = useState<string | undefined>(undefined);
  const urls = useStillUrls(held);

  const settled = useDebounced(questions, DEBOUNCE_MS);
  const { titleId, doc, docVersion } = ctx;
  const qProblems = useMemo(
    () => questionProblems(settled, { titleId, doc, docVersion, guidePages }),
    [settled, titleId, doc, docVersion, guidePages],
  );

  const stillOf = (d: SceneDraft) => (d.stillId === undefined ? undefined : held.get(d.stillId));
  const serverAt = (i: number, field: string) =>
    server.some((p) => p.at === "scene" && p.index === i && p.field === field);
  const textSize = JSON.stringify(drafts).length + questions.length + 4096;
  const size = bodySize(drafts, held, textSize);
  const current = snapshot(drafts, questions, cv);
  const over = size > BODY_MAX_BYTES || tooBigAt === current;
  /** The typed question set has not been through the parser yet: nothing is saved until it has. */
  const checking = settled !== questions;
  const lastReplaced = [...replaced.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];

  const messages: SceneMessages[] = drafts.map((d, i) => {
    const still = stillOf(d);
    const m = sceneMessages(d, sceneProblems(d, still, guidePages), still, guidePages);
    const r = refused.get(i);
    if (r !== undefined)
      m.still =
        r.reason === "size" ? t("edit.still.tooBig", { size: mb(r.size) }) : t("edit.still.type");
    else if (over && lastReplaced === i) m.still = t("edit.still.bodyOver");
    else if (serverAt(i, "still")) m.still = t("edit.still.server");
    if (m.pages === undefined && serverAt(i, "pages")) m.pages = t("edit.pages.server");
    if (m.narration === undefined && serverAt(i, "narration"))
      m.narration = t("edit.narration.server");
    if (m.box === undefined && serverAt(i, "box")) m.box = t("edit.box.server");
    return m;
  });
  const cvMessage =
    cv.trim() === ""
      ? t("edit.cv.empty")
      : server.some((p) => p.at === "contentVersion")
        ? t("edit.cv.server")
        : undefined;
  const qServer = server.some((p) => p.at === "questions");

  const problems: Problem[] = [];
  messages.forEach((m, i) => {
    const n = i + 1;
    const at = (part: string) => `s${String(i)}-${part}`;
    if (m.still !== undefined)
      problems.push({ label: t("edit.prob.still", { n }), go: { target: at("file"), scene: i } });
    if (m.narration !== undefined)
      problems.push({
        label: t("edit.prob.narration", { n }),
        go: { target: at("narr"), scene: i },
      });
    if (m.pages !== undefined)
      problems.push({ label: t("edit.prob.pages", { n }), go: { target: at("pages"), scene: i } });
    if (m.box !== undefined)
      problems.push({ label: t("edit.prob.box", { n }), go: { target: at("x"), scene: i } });
  });
  const firstQ = qProblems[0];
  if (firstQ !== undefined)
    problems.push({
      label:
        qProblems.length === 1
          ? t("edit.prob.questionsLine", { line: firstQ.line })
          : t("edit.prob.questions", { n: qProblems.length }),
      go: { target: QUESTIONS_ID, line: firstQ.line },
    });
  else if (qServer)
    problems.push({ label: t("edit.prob.questionsServer"), go: { target: QUESTIONS_ID } });
  if (cvMessage !== undefined) problems.push({ label: t("edit.prob.cv"), go: { target: "cv" } });
  if (over && lastReplaced === undefined)
    problems.push({ label: t("edit.prob.size"), go: { target: "sizeCard" } });

  const dirty = current !== saved || replaced.size > 0;
  const gone = banner?.kind === "conflict";

  const go = ({ target, scene, line }: Go) => {
    if (scene !== undefined && scene !== open)
      flushSync(() => {
        setOpen(scene);
      });
    if (line !== undefined) focusLine(line);
    else document.getElementById(target)?.focus();
  };

  /** Clears the server's marks on what the drafter has just changed. */
  const unmark = (keep: (p: FieldPlace) => boolean) => {
    setServer((s) => s.filter(keep));
  };

  /** Only the GET carries guidePages; a save's or submit's answer keeps the ones the page read. */
  const withPages = (next: VersionDetail): VersionDetail =>
    next.guidePages === undefined && guidePages !== undefined ? { ...next, guidePages } : next;

  const saveMutation = useMutation({
    mutationFn: (body: ReturnType<typeof buildBody>) =>
      api.put(versionPath(id), VersionDetail, body),
  });
  const submitMutation = useMutation({
    mutationFn: () => api.post(`${versionPath(id)}/submit`, VersionDetail, {}),
  });
  const busy = saveMutation.isPending || submitMutation.isPending;

  const refusedBy = (err: unknown, submitting: boolean) => {
    if (err instanceof ConflictError && (err.fields.length === 0 || err.fields.includes("state"))) {
      setBanner({ kind: "conflict" });
      return;
    }
    if (err instanceof ForbiddenError) {
      setBanner({ kind: "forbidden" });
      return;
    }
    if (
      (err instanceof ValidationError && err.source === "request") ||
      err instanceof ConflictError
    ) {
      const places = err.fields.map((f) => fieldOf(f, drafts));
      setServer(places.filter((p) => p.at !== "other"));
      setBanner(
        submitting
          ? { kind: "submit-refused" }
          : { kind: "refused", mapped: places.length > 0 && places.every((p) => p.at !== "other") },
      );
      return;
    }
    setBanner({ kind: "failed" });
  };

  const save = async (): Promise<boolean> => {
    if (questionProblems(questions, ctx).length > 0 || problems.length > 0) return false;
    const body = buildBody(version, drafts, held, questions, cv);
    if (new Blob([JSON.stringify(body)]).size > BODY_MAX_BYTES) {
      // The estimate said it fits and the exact body does not: say so, as the estimate would have.
      setTooBigAt(current);
      return false;
    }
    try {
      const next = await saveMutation.mutateAsync(body);
      queryClient.setQueryData(versionKey(id), withPages(next));
      void queryClient.invalidateQueries({ queryKey: versionsKey(version.titleId) });
      setSaved(snapshot(drafts, questions, cv));
      setReplaced(new Map());
      setServer([]);
      setBanner(null);
      setSavedOnce(true);
      say(t("edit.announce.saved"));
      return true;
    } catch (err) {
      refusedBy(err, false);
      return false;
    }
  };

  const submit = async () => {
    if (dirty && !(await save())) return;
    try {
      const next = await submitMutation.mutateAsync();
      queryClient.setQueryData(versionKey(id), withPages(next));
      void queryClient.invalidateQueries({ queryKey: versionsKey(version.titleId) });
      onSubmitted(next);
    } catch (err) {
      refusedBy(err, true);
    }
  };

  const onFile = async (i: number, file: File) => {
    const name = file.name;
    if (file.size > STILL_MAX_BYTES) {
      setRefused((r) => new Map(r).set(i, { name, reason: "size", size: file.size }));
      return;
    }
    setReading((r) => new Set(r).add(i));
    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      const read = await holdStill("pending", bytes, name);
      if ("refused" in read) {
        setRefused((r) =>
          new Map(r).set(i, { name, reason: read.refused, size: bytes.byteLength }),
        );
        return;
      }
      const still: HeldStill = { ...read, id: replacedStillId(read.sha256) };
      // Only now, read and hashed, may the scene name it.
      setHeld((h) => new Map(h).set(still.id, still));
      setDrafts((ds) => ds.map((d, k) => (k === i ? { ...d, stillId: still.id } : d)));
      setReplaced((r) => new Map(r).set(i, Math.max(0, ...r.values()) + 1));
      setRefused((r) => {
        const next = new Map(r);
        next.delete(i);
        return next;
      });
      unmark((p) => !(p.at === "scene" && p.index === i && p.field === "still"));
      say(t("edit.announce.still", { n: i + 1, name }));
    } finally {
      setReading((r) => {
        const next = new Set(r);
        next.delete(i);
        return next;
      });
    }
  };

  const setDraft = (i: number, next: SceneDraft) => {
    const before = drafts[i];
    setDrafts((ds) => ds.map((d, k) => (k === i ? next : d)));
    if (before === undefined) return;
    unmark(
      (p) =>
        !(
          p.at === "scene" &&
          p.index === i &&
          ((p.field === "pages" && next.pages !== before.pages) ||
            (p.field === "narration" && next.narration !== before.narration) ||
            (p.field === "box" && next.box !== before.box))
        ),
    );
  };

  const n = problems.length;
  const saveOff = gone || busy || checking || n > 0 || !dirty || reading.size > 0;
  const submitOff = gone || busy || checking || n > 0 || reading.size > 0;
  const why = gone
    ? t("edit.bar.gone")
    : n > 0
      ? t(n === 1 ? "edit.bar.fixOne" : "edit.bar.fix")
      : checking
        ? t("edit.bar.checking")
        : dirty
          ? t("edit.bar.saveFirst")
          : t("edit.bar.noChanges");
  const sentBack = version.sentBack;

  return (
    <section aria-labelledby="page-title" className="grid gap-3.5 pb-14">
      <Head version={version} title={title} />

      {sentBack === undefined ? null : (
        <div
          role="note"
          aria-label={t("edit.sentBackLabel")}
          className="flex items-start gap-2.5 rounded-8 border border-wp-accent/44 bg-wp-accent/13 px-3 py-2.5 text-14 text-wp-text"
        >
          <Icon name="replay" size={18} className="mt-0.5 shrink-0 text-wp-accent" />
          <span>
            <b className="font-semibold">
              {t("edit.sentBack", {
                who: whoOf(sentBack.by, principal.userId),
                when: formatWhen(sentBack.at),
              })}
            </b>{" "}
            {sentBack.reason}
          </span>
        </div>
      )}

      {banner === null ? null : <BannerView banner={banner} id={id} />}

      <div
        role="region"
        aria-label={t("edit.bar.label")}
        className="z-10 flex flex-wrap items-center gap-x-3.5 gap-y-2 rounded-12 border border-wp-line bg-wp-bg/92 px-3.5 py-2.5 backdrop-blur-md min-[721px]:sticky min-[721px]:top-0"
      >
        <div className="grid min-w-0 flex-[1_1_340px] gap-1">
          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5 text-13 text-wp-text-2">
            {gone ? (
              <StatusChip tone="stop">{t("edit.bar.notSaved")}</StatusChip>
            ) : n > 0 ? (
              <StatusChip tone="stop">
                {n === 1 ? t("edit.bar.problem") : t("edit.bar.problems", { n })}
              </StatusChip>
            ) : dirty ? (
              <StatusChip tone="open">{t("edit.bar.unsaved")}</StatusChip>
            ) : (
              <StatusChip tone="done">{t("edit.bar.saved")}</StatusChip>
            )}
            <span id="saveWhy" className="text-12 text-wp-text-3">
              {savedOnce || dirty || n > 0 || gone ? why : null}
            </span>
          </div>
          {n > 0 && !gone ? (
            <ul
              aria-label={t("edit.bar.problemsLabel")}
              className="m-0 flex list-none flex-wrap gap-x-3.5 gap-y-1 p-0 text-13"
            >
              {problems.map((p) => (
                <li key={p.label}>
                  <a
                    href={`#${p.go.target}`}
                    onClick={(e) => {
                      e.preventDefault();
                      go(p.go);
                    }}
                    className={cn("text-stop-soft underline underline-offset-2", focusRing)}
                  >
                    {p.label}
                  </a>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
        <div className="flex w-full flex-wrap items-center gap-2 min-[721px]:w-auto">
          <Button
            variant="quiet"
            onClick={() => {
              if (dirty && !gone) setAsking(true);
              else navigate(reviewPath(id));
            }}
          >
            {t(gone ? "edit.back" : "edit.cancel")}
          </Button>
          <Button
            variant={saveOff ? "secondary" : "primary"}
            aria-disabled={saveOff}
            aria-describedby="saveWhy"
            onClick={() => {
              if (!saveOff) void save();
            }}
            className={OFF}
          >
            {saveMutation.isPending ? t("edit.saving") : t("edit.save")}
          </Button>
          <Button
            aria-disabled={submitOff}
            aria-describedby="saveWhy"
            onClick={() => {
              if (!submitOff) void submit();
            }}
            className={OFF}
          >
            {submitMutation.isPending
              ? t("edit.submitting")
              : t(dirty ? "edit.saveSubmit" : "edit.submit")}
          </Button>
        </div>
        {asking ? (
          <div
            role="group"
            aria-label={t("edit.discard.label")}
            className="flex basis-full flex-wrap items-center justify-end gap-2 text-13"
          >
            <span>{t("edit.discard.ask")}</span>
            <Button
              compact
              autoFocus
              className="border-stop/62 text-stop-soft"
              onClick={() => {
                navigate(reviewPath(id));
              }}
            >
              {t("edit.discard")}
            </Button>
            <Button
              compact
              variant="quiet"
              onClick={() => {
                setAsking(false);
              }}
            >
              {t("edit.keep")}
            </Button>
          </div>
        ) : null}
      </div>

      <div className="grid items-start gap-5 min-[1181px]:grid-cols-[minmax(0,1fr)_minmax(270px,320px)]">
        <div className="grid min-w-0 gap-4">
          <section aria-labelledby="scenesHd" className={cn(CARD, "min-w-0")}>
            <h2 id="scenesHd" className="m-0 px-4 pt-3.5 text-14 font-semibold">
              {t("edit.scenes.title")}
            </h2>
            <div className="px-4 pt-3.5 pb-4">
              <p className={cn(LEAD, "mb-3")}>
                {guidePages === undefined
                  ? t("edit.scenes.leadNoPages")
                  : t("edit.scenes.lead", { pages: guidePages })}
              </p>
              <ol className="m-0 grid list-none gap-2.5 p-0">
                {version.script.scenes.map((scene, i) => {
                  const d = drafts[i];
                  if (d === undefined) return null;
                  const still = stillOf(d);
                  return (
                    <SceneEditor
                      key={i}
                      index={i}
                      scene={scene}
                      draft={d}
                      still={still}
                      stillUrl={d.stillId === undefined ? undefined : urls.get(d.stillId)}
                      replaced={replaced.has(i)}
                      refusedFile={refused.get(i)?.name}
                      reading={reading.has(i)}
                      messages={messages[i] ?? {}}
                      guidePages={guidePages}
                      changed={
                        JSON.stringify(d) !== JSON.stringify(draftOf(scene)) || replaced.has(i)
                      }
                      open={open === i}
                      onToggle={() => {
                        setOpen(open === i ? -1 : i);
                      }}
                      onChange={(next) => {
                        setDraft(i, next);
                      }}
                      onFile={(file) => {
                        void onFile(i, file);
                      }}
                      onAddBox={() => {
                        if (still === undefined) return;
                        flushSync(() => {
                          setDraft(i, { ...d, box: centredBox(still) });
                        });
                        document.getElementById(`s${String(i)}-x`)?.focus();
                        say(t("edit.announce.box", { n: i + 1 }));
                      }}
                      onNoBox={() => {
                        flushSync(() => {
                          setDraft(i, { ...d, box: null });
                        });
                        document.getElementById(`s${String(i)}-add`)?.focus();
                        say(t("edit.announce.noBox", { n: i + 1 }));
                      }}
                    />
                  );
                })}
              </ol>
            </div>
          </section>

          <section aria-labelledby="qsHd" className={cn(CARD, "min-w-0")}>
            <h2 id="qsHd" className="m-0 px-4 pt-3.5 text-14 font-semibold">
              {t("edit.qs.title")}
            </h2>
            <div className="px-4 pt-3.5 pb-4">
              <p className={cn(LEAD, "mb-3")}>{t("edit.qs.lead")}</p>
              <QuestionsEditor
                markdown={questions}
                problems={qProblems}
                serverRefused={qServer}
                onChange={(md) => {
                  setQuestions(md);
                  if (qServer) unmark((p) => p.at !== "questions");
                }}
              />
            </div>
          </section>
        </div>

        <aside
          aria-label={t("edit.side")}
          className="grid min-w-0 gap-4 min-[1181px]:sticky min-[1181px]:top-24"
        >
          <section aria-labelledby="cvHd" className={CARD}>
            <h2 id="cvHd" className="m-0 px-4 pt-3.5 text-14 font-semibold">
              {t("edit.cv.title")}
            </h2>
            <div className="px-4 pt-3.5 pb-4">
              <Label htmlFor="cv">{t("edit.cv.label")}</Label>
              <input
                id="cv"
                value={cv}
                autoComplete="off"
                spellCheck={false}
                aria-invalid={cvMessage !== undefined}
                aria-describedby="cvRule cvNow cvErr"
                onChange={(e) => {
                  setCv(e.target.value);
                  unmark((p) => p.at !== "contentVersion");
                }}
                className={cn(FIELD, "max-w-36 font-mono")}
              />
              <FieldError id="cvErr">{cvMessage}</FieldError>
              <div id="cvRule">
                <p className="mt-2.5 mb-0 text-13 font-semibold text-wp-text">
                  {t("review.decide.teaches")}
                </p>
                <ul className="mt-2 mb-0 grid list-none gap-1.5 p-0 text-13 text-wp-text-2">
                  <li>{t("review.decide.teachesYes")}</li>
                  <li>{t("review.decide.teachesNo")}</li>
                </ul>
                <p className="mt-2 mb-0 text-13 text-wp-text-2">
                  {served === undefined
                    ? t("edit.cv.howNoServed")
                    : t("edit.cv.how", { cv: served })}
                </p>
              </div>
              <p id="cvNow" className="mt-2 mb-0 text-12 text-wp-text-3">
                {served === undefined || cv.trim() === ""
                  ? null
                  : cv.trim() === served
                    ? t("edit.cv.kept", { cv: served })
                    : t("edit.cv.raised", { from: served, to: cv.trim() })}
              </p>
            </div>
          </section>

          <section aria-labelledby="sizeHd" id="sizeCard" tabIndex={-1} className={CARD}>
            <h2 id="sizeHd" className="m-0 px-4 pt-3.5 text-14 font-semibold">
              {t("edit.size.title")}
            </h2>
            <div className="px-4 pt-3.5 pb-4">
              <p className={LEAD}>{t("edit.size.line", { size: mb(size) })}</p>
              <div
                aria-hidden
                className="mt-2 mb-1.5 h-1.5 overflow-hidden rounded-pill bg-wp-bg/58"
              >
                <i
                  className={cn("block h-full", over ? "bg-stop-soft" : "bg-wp-accent")}
                  style={{ width: `${String(Math.min(100, (size / BODY_MAX_BYTES) * 100))}%` }}
                />
              </div>
              {over ? (
                <FieldError id="sizeErr">
                  {lastReplaced === undefined ? t("edit.size.over") : t("edit.still.bodyOver")}
                </FieldError>
              ) : (
                <p className="m-0 text-12 text-wp-text-3">{t("edit.size.each")}</p>
              )}
            </div>
          </section>

          <section aria-labelledby="histHd" className={CARD}>
            <h2 id="histHd" className="m-0 px-4 pt-3.5 text-14 font-semibold">
              {t("review.history")}
            </h2>
            <ul className="m-0 grid list-none gap-1.5 px-4 pt-3.5 pb-4 text-13 text-wp-text-2">
              {trailOf(version).map((entry) => (
                <li key={`${entry.kind}-${entry.at}`} className="grid grid-cols-[auto_1fr] gap-2">
                  <time dateTime={entry.at} className="text-12 whitespace-nowrap text-wp-text-3">
                    {formatWhen(entry.at)}
                  </time>
                  <span>{trailWords(entry, principal.userId)}</span>
                </li>
              ))}
            </ul>
          </section>
        </aside>
      </div>
    </section>
  );
}

/** An aria-disabled button stays focusable, so its reason is read, but must look off (mockup). */
const OFF =
  "aria-disabled:cursor-not-allowed aria-disabled:border-wp-line aria-disabled:bg-transparent aria-disabled:text-wp-text-3 aria-disabled:hover:bg-transparent aria-disabled:active:translate-y-0";

function BannerView({ banner, id }: { banner: Banner; id: string }) {
  const stop = "rounded-8 border border-stop/48 bg-stop/20 px-3 py-2.5 text-13 text-wp-text";
  switch (banner.kind) {
    case "conflict":
      return (
        <div role="alert" className={stop}>
          <b className="mb-0.5 block">{t("edit.conflict.title")}</b>
          {t("edit.conflict.line")}
          <div className="mt-2">
            <Button asChild compact>
              <Link href={reviewPath(id)}>{t("edit.conflict.open")}</Link>
            </Button>
          </div>
        </div>
      );
    case "refused":
      return (
        <div role="alert" className={stop}>
          <b className="mb-0.5 block">{t("edit.refused.title")}</b>
          {t(banner.mapped ? "edit.refused.fields" : "edit.refused.other")}
        </div>
      );
    case "submit-refused":
      return (
        <div role="alert" className={stop}>
          <b className="mb-0.5 block">{t("edit.refused.submit")}</b>
          {t("edit.refused.fields")}
        </div>
      );
    case "forbidden":
      return (
        <div role="alert" className={stop}>
          <b className="mb-0.5 block">{t("edit.refused.title")}</b>
          {t("edit.refused.forbidden")}
        </div>
      );
    case "failed":
      return (
        <div role="alert" className={stop}>
          <b className="mb-0.5 block">{t("edit.refused.title")}</b>
          {t("edit.refused.failed")}
        </div>
      );
  }
}
